import { ChevronLeft, ChevronRight, Close, Event } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import "dayjs/locale/pt-br";

import {
  getMobileSheetSlideProps,
  getMobileSheetTransitionDuration,
} from "@/components/common/mobileSheetMotion";

export interface WearablePeriodDateRowProps {
  periodLabel: string;
  periodEnd: Dayjs;
  today: Dayjs;
  loading: boolean;
  onPrevDay: () => void;
  onNextDay: () => void;
  onPickDate: (date: Dayjs) => void;
}

/**
 * Navegação de período wearable: setas movem o fim (âncora); label exibe intervalo retroativo.
 */
export function WearablePeriodDateRow({
  periodLabel,
  periodEnd,
  today,
  loading,
  onPrevDay,
  onNextDay,
  onPickDate,
}: WearablePeriodDateRowProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(periodEnd);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (drawerOpen) setDraftDate(periodEnd);
  }, [drawerOpen, periodEnd]);

  const navButtonSx = {
    color: "white",
    bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
      alpha(t.palette.neutral0.main, 0.12),
    "&:hover": {
      bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
        alpha(t.palette.neutral0.main, 0.2),
    },
    "&.Mui-disabled": {
      color: (t: { palette: { neutral0: { main: string } } }) =>
        alpha(t.palette.neutral0.main, 0.3),
      bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
        alpha(t.palette.neutral0.main, 0.06),
    },
  };

  const periodFieldSx = {
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: { xs: "100%", sm: 280 },
    color: "white",
    bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
      alpha(t.palette.neutral0.main, 0.12),
    borderRadius: 2,
    minHeight: 36,
    px: 1,
    py: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    cursor: loading ? "default" : "pointer",
    "&:hover": {
      bgcolor: (t: { palette: { neutral0: { main: string } } }) =>
        alpha(t.palette.neutral0.main, 0.2),
    },
  };

  const handleOpenDatePicker = () => {
    if (loading) return;
    if (isMobile) {
      setDrawerOpen(true);
      return;
    }
    setDatePickerOpen(true);
  };

  const handleConfirmDrawerDate = () => {
    const clamped = draftDate.isAfter(today, "day")
      ? today.startOf("day")
      : draftDate.startOf("day");
    onPickDate(clamped);
    setDrawerOpen(false);
  };

  const isNextDisabled = loading || periodEnd.isSame(today, "day");

  return (
    <>
      <Stack
        direction="row"
        spacing={0.65}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ minWidth: 0, flexShrink: 0 }}
      >
        <IconButton
          size="small"
          disabled={loading}
          onClick={onPrevDay}
          aria-label="Período anterior"
          sx={{ flexShrink: 0, p: 0.5, ...navButtonSx }}
        >
          <ChevronLeft sx={{ fontSize: 20 }} />
        </IconButton>

        <Box ref={anchorRef} sx={{ position: "relative", minWidth: 0 }}>
          <Box
            role="button"
            tabIndex={loading ? -1 : 0}
            aria-label="Selecionar data final do período"
            onClick={handleOpenDatePicker}
            onKeyDown={(event) => {
              if (loading) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleOpenDatePicker();
              }
            }}
            sx={periodFieldSx}
          >
            <Event sx={{ color: "white", fontSize: 16, flexShrink: 0 }} />
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.72rem", sm: "0.8rem" },
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {periodLabel}
            </Typography>
          </Box>

          {!isMobile && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <DatePicker
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                value={periodEnd}
                maxDate={today}
                disabled={loading}
                onChange={(newDate) => {
                  if (newDate) {
                    onPickDate(newDate);
                    setDatePickerOpen(false);
                  }
                }}
                format="DD/MM/YYYY"
                sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }}
                slotProps={{
                  popper: {
                    anchorEl: anchorRef.current,
                    sx: {
                      "& .MuiPaper-root": {
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
                        borderRadius: 2,
                      },
                    },
                  },
                  textField: { sx: { display: "none" } },
                }}
              />
            </LocalizationProvider>
          )}
        </Box>

        <IconButton
          size="small"
          disabled={isNextDisabled}
          onClick={onNextDay}
          aria-label="Próximo período"
          sx={{ flexShrink: 0, p: 0.5, ...navButtonSx }}
        >
          <ChevronRight sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      {isMobile && (
        <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => undefined}
        disableSwipeToOpen
        swipeAreaWidth={0}
        transitionDuration={getMobileSheetTransitionDuration()}
        SlideProps={getMobileSheetSlideProps(theme)}
        ModalProps={{
          keepMounted: false,
          sx: { zIndex: theme.zIndex.modal + 4 },
          BackdropProps: {
            sx: { bgcolor: alpha(theme.palette.common.black, 0.5) },
            transitionDuration: getMobileSheetTransitionDuration(),
          },
        }}
        PaperProps={{
          sx: {
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "20px 20px 0 0",
            maxHeight: "88dvh",
            bgcolor: "background.paper",
            boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.18)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 99,
              bgcolor: "divider",
            }}
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Data final
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Escolha o último dia do período
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Fechar"
            onClick={() => setDrawerOpen(false)}
            sx={{ flexShrink: 0 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            px: 1,
            py: 0.5,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <StaticDatePicker
              displayStaticWrapperAs="mobile"
              value={draftDate}
              maxDate={today}
              onChange={(value) => {
                if (value) setDraftDate(dayjs(value).startOf("day"));
              }}
              slotProps={{
                toolbar: { hidden: true },
                actionBar: { actions: [] },
              }}
              sx={{
                width: "100%",
                maxWidth: 360,
                "& .MuiPickersCalendarHeader-root": {
                  px: 0.5,
                },
                "& .MuiDayCalendar-root": {
                  width: "100%",
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            px: 2,
            pt: 1,
            pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setDrawerOpen(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleConfirmDrawerDate}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            Confirmar
          </Button>
        </Stack>
      </SwipeableDrawer>
      )}
    </>
  );
}
